import { type ReactElement, useState } from "react";

import { Combobox } from "@similarweb/ui-components";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import type { SignalTimeframe } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframe";

import { DROPDOWN_AUTO_OPEN_DELAY } from "../../../constants/dropdown";
import { StyledSignalFilterBlock } from "../shared/styles";
import FilterButtonLabel from "../shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "../shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";

interface Props {
    selectedTimeframe: SignalTimeframe | undefined;
    options: readonly SignalTimeframe[];
    autoOpen?: boolean;
    onSelect(timeframe: SignalTimeframe): void;
}

const TIMEFRAME_DROPDOWN_WIDTH = "180px";

const toOptionId = (timeframe: SignalTimeframe): string => `${timeframe.id}-${timeframe.amount}`;

const getTimeframeTranslatable = (timeframe: SignalTimeframe) => ({
    key: `si.signals.timeframe.dropdown.${timeframe.id}-${timeframe.amount}`,
});

const SignalTimeframeSelect = ({ selectedTimeframe, options, autoOpen = false, onSelect }: Props): ReactElement => {
    const [isOpen, setIsOpen] = useState(false);
    const hasValue = typeof selectedTimeframe !== "undefined";

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, () => setIsOpen(true), autoOpen);

    const handleSelect = (timeframe: SignalTimeframe): void => {
        setIsOpen(false);
        onSelect(timeframe);
    };

    const selectedOptionId = selectedTimeframe ? toOptionId(selectedTimeframe) : undefined;

    return (
        <StyledSignalFilterBlock id="signal-timeframe">
            <Combobox
                data-automation="signal-timeframe-dd"
                placement="bottom-start"
                width={TIMEFRAME_DROPDOWN_WIDTH}
                isOpen={isOpen}
                onOpenChange={setIsOpen}
            >
                <Combobox.Trigger>
                    <SignalFilterDropdownButton
                        id="signal-timeframe"
                        isOpen={isOpen}
                        hasValue={hasValue}
                    >
                        {hasValue ? (
                            <I18n
                                data-automation="signal-timeframe-dd-button-text"
                                element={FilterButtonLabel}
                                translatable={getTimeframeTranslatable(selectedTimeframe)}
                            />
                        ) : null}
                    </SignalFilterDropdownButton>
                </Combobox.Trigger>

                <Combobox.Content selectionMode="single">
                    {options.map((option) => {
                        const optionId = toOptionId(option);

                        return (
                            <Combobox.Option
                                key={optionId}
                                id={optionId}
                                textValue={getTimeframeTranslatable(option).key}
                                isSelected={optionId === selectedOptionId}
                                onClick={() => handleSelect(option)}
                            >
                                <Combobox.OptionLabel>
                                    <I18n
                                        data-automation="signal-timeframe-dd-item-text"
                                        translatable={getTimeframeTranslatable(option)}
                                    />
                                </Combobox.OptionLabel>
                            </Combobox.Option>
                        );
                    })}
                </Combobox.Content>
            </Combobox>
        </StyledSignalFilterBlock>
    );
};

export default SignalTimeframeSelect;
